/**
 * @file: FacadeWidget.tsx
 *
 * Renders the vehicle exterior-status diagram (车辆外观状态) a chat bot posts
 * via a ` ```facade ` fence in its pre-inspection summary: the top-down car
 * view flanked by two side views, with abnormal body regions tinted. Reuses
 * the report's `VehicleFacadeBirdView` and its name→overlay config, so the
 * diagram is identical to the one in the pre-inspection report.
 *
 * Fence payload: `{ title?, regions: [{ name, severity: 'urgent'|'warning' }] }`
 * where `name` is the facade site display name (e.g. 前保险杠).
 */

import { VehicleFacadeBirdView } from '@euler/app/flows/report/components/VehicleFacadeBirdView';
import { Overlay } from '@euler/app/flows/report/components/VehicleFacadeBirdView.shared';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FacadeSiteConfigurationMapByName } from '@euler/model';
import React, { memo, useContext, useMemo } from 'react';
import { Text, View } from 'react-native';
import { ChartWidthContext } from './ChartWidget';
import { FacadePayload, FacadeRegion } from './payloads';

/** Severity → overlay tint (matches the report diagram). */
const SEVERITY_COLOR: Record<string, string> = {
  urgent: '#e0463d',
  warning: '#ff8a00',
};

/** Cap the (square) canvas so a wide bubble doesn't balloon it. */
const FACADE_MAX_WIDTH = 320;

export const FacadeWidget = memo(({ data }: { data: FacadePayload }) => {
  const { rpx } = useResponsiveContext();
  const boundedWidth = useContext(ChartWidthContext);
  const title: string = data?.title || '车辆外观状态';

  // Map each abnormal region (by site name) to a tinted overlay, reusing the
  // report's name→config table. Unknown names are skipped defensively.
  const overlays: Overlay[] = useMemo(() => {
    const regions: FacadeRegion[] = Array.isArray(data?.regions)
      ? data.regions
      : [];
    return regions
      .map(r => {
        const config = FacadeSiteConfigurationMapByName.get(r.name);
        if (config == null) return null;
        return {
          name: config.name,
          imagePath: config.overlay,
          coords: config.coords,
          color: SEVERITY_COLOR[r.severity] ?? SEVERITY_COLOR.warning,
        };
      })
      .filter((o): o is Overlay => o != null);
  }, [data]);

  const size = Math.min(boundedWidth ?? FACADE_MAX_WIDTH, FACADE_MAX_WIDTH);

  return (
    <View
      style={{
        backgroundColor: '#F8FAFC',
        borderRadius: rpx(10),
        paddingVertical: rpx(12),
        marginVertical: rpx(6),
        alignItems: 'center',
      }}
    >
      <Text
        style={{
          fontSize: rpx(14),
          fontWeight: '600',
          color: '#334155',
          marginBottom: rpx(8),
        }}
      >
        {title}
      </Text>
      {/* Fixed-size, clipped box: gives the list a deterministic item height
          and (on web) contains the renderer's off-screen canvas — which sits
          at `top: -9999rem` and would otherwise escape its virtualized cell,
          inflate the scroll area, and make the thread jitter via the
          browser's scroll-anchoring. */}
      <View
        style={{
          width: size,
          height: size,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <VehicleFacadeBirdView overlays={overlays} size={size} />
      </View>
    </View>
  );
});
